import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

interface ArticleData {
  id: string;
  title: string;
  cover: string;
  tag: string;
  views: string;
  updateTime: string;
  category: string;
  slug: string;
  excerpt: string;
  author: string;
  publishedAt: string;
  content: string;
  relatedVideos?: Array<{
    id: string;
    title: string;
    cover: string;
    tag: string;
    views: string;
    updateTime: string;
    category: string;
    slug: string;
    excerpt: string;
    author: string;
    publishedAt: string;
  }>;
  timeline?: Array<{ time: string; event: string }>;
  hotComments?: Array<{ user: string; content: string; likes: number }>;
}

const categoryLabelMap: Record<string, string> = {
  wanghong: '网红黑料',
  mingxing: '明星八卦',
  remen: '热门吃瓜',
  guochan: '国产吃瓜',
  tafang: '塌房现场',
  heji: '吃瓜合集',
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setArticle(null);
    import('@/mocks/zh/pages').then((mod) => {
      const data = mod.articleData as Record<string, ArticleData>;
      setArticle(data[slug] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <i className="ri-loader-4-line animate-spin text-3xl text-foreground-400"></i>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <SEO title="文章未找到 - 吃瓜视频" description="该文章不存在或已被删除" keywords="吃瓜视频" canonicalUrl={`/article/${slug}`} />
        <Navbar />
        <main className="pt-16 min-h-screen flex flex-col items-center justify-center bg-background-50 px-4">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被删除</p>
          <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishedAt,
      dateModified: article.publishedAt,
      publisher: {
        '@type': 'Organization',
        name: '吃瓜视频',
        url: 'https://chigua-spa.com',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://chigua-spa.com/article/${article.slug}`,
      },
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, item: { '@id': 'https://chigua-spa.com', name: '首页' } },
        { '@type': 'ListItem', position: 2, item: { '@id': `https://chigua-spa.com/category/${article.category}`, name: categoryLabelMap[article.category] || article.category } },
        { '@type': 'ListItem', position: 3, item: { '@id': `https://chigua-spa.com/article/${article.slug}`, name: article.title } },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${article.title} - 吃瓜视频`}
        description={article.excerpt}
        keywords={`吃瓜视频在线, 吃瓜视频网站, 国产吃瓜视频, ${article.tag}, ${categoryLabelMap[article.category] || ''}`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.cover}
        publishedTime={article.publishedAt}
        modifiedTime={article.publishedAt}
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryLabelMap[article.category] || article.category, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <div className="lg:w-[42%]">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap">
                  {article.tag}
                </span>
                <span className="text-xs text-foreground-500">{article.views} 次播放</span>
                <span className="text-xs text-foreground-500">{article.updateTime}</span>
              </div>

              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-background-200/70">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                  <div className="text-xs text-foreground-500">{article.publishedAt}</div>
                </div>
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-8"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {article.timeline && article.timeline.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-timeline-view text-primary-500"></i>
                    事件时间线
                  </h3>
                  <div className="relative pl-6 border-l-2 border-accent-200">
                    {article.timeline.map((item, idx) => (
                      <div key={idx} className="relative mb-5 last:mb-0">
                        <div className="absolute -left-[25px] top-1 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-background-50"></div>
                        <span className="text-xs font-semibold text-accent-600 block mb-0.5">{item.time}</span>
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:w-[58%]">
              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 吃瓜视频`}
                  className="w-full aspect-video object-cover object-top"
                />
              </div>

              {article.relatedVideos && article.relatedVideos.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                    相关视频
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {article.relatedVideos.map((rv) => (
                      <Link
                        key={rv.id}
                        to={`/article/${rv.slug}`}
                        className="group flex gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                      >
                        <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={rv.cover}
                            alt={rv.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {rv.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                            <span>{rv.author}</span>
                            <span>·</span>
                            <span>{rv.views}次</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-5 rounded-xl bg-accent-50/50 border border-accent-100/50 mb-8">
                <h3 className="text-sm font-semibold text-accent-800 mb-3 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  关于{categoryLabelMap[article.category] || '本文章'}
                </h3>
                <p className="text-xs text-accent-700 leading-relaxed">
                  本文由吃瓜视频平台整理发布。我们致力于为用户提供最新、最全的娱乐八卦内容。
                  如发现内容有误或有侵权问题，请通过关于我们页面的联系方式与我们取得联系。
                </p>
              </div>
            </div>
          </div>

          {article.hotComments && article.hotComments.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <div className="max-w-2xl">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热度指数
                  <span className="text-xs font-normal text-foreground-500 ml-2">{article.hotComments.reduce((sum, c) => sum + c.likes, 0).toLocaleString()} 热度</span>
                </h2>
                <div className="flex flex-col gap-4">
                  {article.hotComments.map((comment, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-background-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center">
                          <i className="ri-user-line text-accent-600 text-xs"></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-800">{comment.user}</span>
                        <span className="ml-auto flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-thumb-up-line"></i>
                          {comment.likes.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}